import { makeAutoObservable, reaction, runInAction } from "mobx";
import { Profile, UserGame } from "../models/Profile";
import { agent } from "../api/agent";
import { store } from "./store";
import { UpdateLocationDto } from "../models/UpdateLocationDto";
import { toast } from "react-toastify";
import i18n from "../../i18n";

export default class ProfileStore {
  profile: Profile | null = null;
  loadingProfile = false;
  uploading = false;
  loading = false;
  loadingFollowings = false;
  followings: Profile[] = [];
  activeTab = 0;
  userGames: UserGame[] = [];
  loadingGames = false;
  updatingLocation = false;

  constructor() {
    makeAutoObservable(this);

    reaction(
      () => this.activeTab,
      activeTab => {
        if (activeTab === 1 || activeTab === 2) {
          const predicate = activeTab === 1 ? 'followers' : 'following';
          this.loadFollowings(predicate);
        } else {
          this.followings = [];
        }
      }
    )
  }

  setActiveTab = (activeTab: any) => {
    this.activeTab = activeTab;
  }

  get isCurrentUser() {
    if (store.userStore.user && this.profile) {
      return store.userStore.user.userID === this.profile.userID;
    }
    return false;
  }

  loadProfile = async (userID: string | any) => {
    this.loadingProfile = true;
    try {
      const profile = await agent.Profiles.get(userID);
      console.log(profile);
      runInAction(() => {
        this.profile = profile;
        // Ensure friendsCount is initialized if not provided by backend
        if (this.profile && this.profile.friendsCount === undefined) {
          this.profile.friendsCount = 0;
        }
        this.loadingProfile = false;
      })
    } catch (error) {
      console.log(error);
      runInAction(() => this.loadingProfile = false);
    }
  }

  deleteAccount = async () => {
    this.loading = true;
    try {
      await agent.Account.deleteAccount();

      runInAction(() => {
        // Clear all user data from stores
        store.userStore.logout();
        this.profile = null;
        this.followings = [];
        this.userGames = [];
        this.loading = false;
      });


      // Clear FCM token and related notification data from localStorage
      this.clearNotificationData();

      toast.success((i18n as any).t('profile.accountDeleted'));

      window.location.href = '/';

    } catch (error: any) {
      toast.error((i18n as any).t('profile.accountDeleteError'));
      runInAction(() => this.loading = false);
    }
  }

  updateFavoriteSports = async (sportIds: string[]) => {
    this.loading = true;
    try {
      await agent.Profiles.updateFavoriteSports(sportIds);

      // Fetch samo sports podatke bez full profile reload
      const updatedProfile = await agent.Profiles.get(this.profile!.userID);

      runInAction(() => {
        if (this.profile) {
          // Samo ažuriramo sports, sve ostalo ostaje
          this.profile.sports = updatedProfile.sports;
        }
        this.loading = false;
      });

      toast.success((i18n as any).t('favoriteSports.updateSuccess'));
    } catch (error: any) {
      console.log('Failed to update favorite sports:', error);
      toast.error((i18n as any).t('favoriteSports.updateError'));
      runInAction(() => this.loading = false);
    }
  };

  updateSportLevel = async (sportId: string, level: number | null) => {
    this.loading = true;
    try {
      await agent.Profiles.updateSportLevel(sportId, level);
      runInAction(() => {
        if (this.profile) {
          const sport = this.profile.sports.find(s => s.id === sportId);
          if (sport) sport.selfReportedLevel = level;
        }
        this.loading = false;
      });
      toast.success((i18n as any).t('favoriteSports.levelUpdateSuccess'));
    } catch (error) {
      console.log(error);
      toast.error((i18n as any).t('favoriteSports.levelUpdateError'));
      runInAction(() => this.loading = false);
    }
  };

  // Helper method to clear all notification-related data from localStorage
  private clearNotificationData = () => {
    try {
      // Common FCM token storage keys - adjust these based on your app's implementation
      const notificationKeys = [
        'fcm_token',
        'fcmToken',
        'firebase_token',
        'firebaseToken',
        'notification_token',
        'notificationToken',
        'push_token',
        'pushToken',
        'messaging_token',
        'messagingToken'
      ];

      // Remove all potential FCM token keys
      notificationKeys.forEach(key => {
        localStorage.removeItem(key);
        sessionStorage.removeItem(key); // Also clear from sessionStorage just in case
      });

      // If you store the token with a user-specific key, clear that too
      if (store.userStore.user?.userID) {
        localStorage.removeItem(`fcm_token_${store.userStore.user.userID}`);
        sessionStorage.removeItem(`fcm_token_${store.userStore.user.userID}`);
      }

      // Clear any other notification-related preferences
      localStorage.removeItem('notification_permission');
      localStorage.removeItem('notifications_enabled');

      console.log((i18n as any).t('profile.fcmTokenCleared'));
    } catch (error) {
      console.log((i18n as any).t('profile.fcmTokenClearError'), error);
    }
  }

  uploadPhoto = async (file: File) => {
    this.uploading = true;
    try {
      const response = await agent.Profiles.uploadPhoto(file);
      const photo = response.data;

      runInAction(() => {
        if (this.profile) {
          if (store.userStore.user) {
            store.userStore.setImage(photo.imageUrl);
            this.profile.image = photo.imageUrl;
          }
        }
        this.uploading = false;
      });
    } catch (error) {
      console.log(error);
      runInAction(() => this.uploading = false);
    }
  }

  updateUserLocation = async (updateLocationDto: UpdateLocationDto) => {
    this.updatingLocation = true;
    try {
      await agent.Profiles.updateUserLocation(updateLocationDto);

      runInAction(() => {
        if (this.profile) {
          store.userStore.setlocation(updateLocationDto);
          this.profile.latitude = updateLocationDto.latitude;
          this.profile.longitude = updateLocationDto.longitude;
        }

        toast.success((i18n as any).t('profile.locationUpdated'));
        this.updatingLocation = false;
      });
    } catch (error: any) {
      console.log('Failed to update location:', error);
      toast.error((i18n as any).t('profile.locationUpdateError'));
      runInAction(() => this.updatingLocation = false);
    }
  }

  updateFollowing = async (userID: string, following: boolean) => {
    this.loading = true;
    try {
      await agent.Profiles.updateFollowing(userID);
      store.gameStore.updateParticipantFollowing(userID);
      runInAction(() => {
        if (this.profile && this.profile.userID !== store.userStore.user?.userID && this.profile.userID === userID) {
          following ? this.profile.followersCount++ : this.profile.followersCount--;
          this.profile.following = !this.profile.following;
        }

        if (this.profile && this.profile.userID === store.userStore.user?.userID) {
          following ? this.profile.followingCount++ : this.profile.followingCount--;
        }

        this.followings.forEach(profile => {
          if (profile.userID === userID) {
            profile.following ? profile.followersCount++ : profile.followersCount--;
            profile.following = !profile.following;
          }
        })
        store.friendStore?.updateFollowingInSearchResults(userID);
        this.loading = false;
      });
    } catch (error) {
      console.log(error);
      runInAction(() => this.loading = false);
    }
  };

  updateFriendsCount = (userID: string, increment: boolean) => {
    runInAction(() => {
      if (this.profile && this.profile.userID === userID) {
        increment ? this.profile.friendsCount++ : this.profile.friendsCount--;
      }

      // Also update in followings list if the user happens to be there
      this.followings.forEach(profile => {
        if (profile.userID === userID) {
          increment ? profile.friendsCount++ : profile.friendsCount--;
        }
      });
    });
  };

  loadFollowings = async (predicate: string) => {
    this.loadingFollowings = true;
    try {
      const followings = await agent.Profiles.listFollowings(this.profile!.userID, predicate);
      runInAction(() => {
        this.followings = followings;
        this.loadingFollowings = false;
      });
    } catch (error) {
      console.log(error);
      runInAction(() => this.loadingFollowings = false);
    }
  }

  loadUserGames = async (userID: string, predicate?: string) => {
    this.loadingGames = true;
    this.userGames = [];
    try {
      const games = await agent.Profiles.listGames(userID, predicate!);
      runInAction(() => {
        this.userGames = games;
        this.loadingGames = false;
      });
    } catch (error) {
      console.log(error);
      runInAction(() => {
        this.loadingGames = false;
      });
    }
  };
}